import type { ReactNode } from 'react';
import type { ExceptionField } from '@/api/cockpit';
import { ConfidenceBadge } from '@/components/common/ConfidenceBadge';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  KEY_HEADER_FIELD_KEYS,
  fieldReasonKey,
  isCustomerAccountField,
  isDateField,
  isSimpleMappedSelectField,
} from '../helpers';
import { FieldValueEditor, type CustomerPickerState } from './FieldValueEditor';
import { FieldReasonPopover } from './FieldReasonPopover';
import { InfoHoverCard } from './InfoHoverCard';
import { MissingFeedbackBadge } from './MissingFeedbackBadge';

/**
 * ADO #700: control surface for the edit-time reason capture flow. The
 * grid wires per-field popovers, the missing-feedback badge, and the
 * debounced/immediate trigger logic for opening the popover.
 */
export interface FieldReasonControls {
  readonly fieldReasons: Record<string, string>;
  readonly fieldReasonDrafts: Record<string, string>;
  readonly missingReasonKeys: ReadonlySet<string>;
  readonly openReasonKey: string | null;
  requestReasonImmediate(reasonKey: string, label: string): void;
  requestReasonDebounced(args: {
    reasonKey: string;
    label: string;
    pendingValue: string;
    previousValue: string;
  }): void;
  dismissReasonPopover(): void;
  setReasonDraft(reasonKey: string, draft: string): void;
  commitReason(reasonKey: string, text: string): void;
}

export interface FieldTileGridProps {
  fields: readonly ExceptionField[];
  fieldValues: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
  highlightedErrorFields: ReadonlySet<string>;
  isFieldEditable: (field: ExceptionField) => boolean;
  customerPicker: CustomerPickerState;
  reasonControls: FieldReasonControls;
}

export function FieldTileGrid(props: FieldTileGridProps): JSX.Element {
  const customerFields = props.fields.filter(isCustomerAccountField);
  const orderFields = props.fields.filter((field) => !isCustomerAccountField(field));

  function handleFieldChange(key: string, value: string): void {
    const field = props.fields.find((candidate) => candidate.key === key);
    props.onFieldChange(key, value);
    if (!field || !KEY_HEADER_FIELD_KEYS.includes(key)) return;
    const reasonKey = fieldReasonKey({ fieldName: key, lineNumber: null });
    const previous = previousHeaderValue(field);
    if (value === previous) {
      // Reverted: no prompt needed; reason auto-clears via the
      // useEffect in useExceptionDetail that watches modifiedKeyFields.
      return;
    }
    if (isSimpleMappedSelectField(field) || isCustomerAccountField(field) || isDateField(field)) {
      props.reasonControls.requestReasonImmediate(reasonKey, field.label);
      return;
    }
    props.reasonControls.requestReasonDebounced({
      reasonKey,
      label: field.label,
      pendingValue: value,
      previousValue: previous,
    });
  }

  return (
    <div className="space-y-4">
      {orderFields.length > 0 ? (
        <FieldGroup
          label="Order details"
          className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
        >
          {orderFields.map((field) => (
            <FieldTile
              key={field.key}
              field={field}
              fieldValues={props.fieldValues}
              onFieldChange={handleFieldChange}
              highlighted={props.highlightedErrorFields.has(field.key)}
              disabled={!props.isFieldEditable(field)}
              customerPicker={props.customerPicker}
              reasonControls={props.reasonControls}
            />
          ))}
        </FieldGroup>
      ) : null}
      {customerFields.length > 0 ? (
        <FieldGroup className="grid gap-3 xl:grid-cols-2">
          {customerFields.map((field) => (
            <FieldTile
              key={field.key}
              field={field}
              fieldValues={props.fieldValues}
              onFieldChange={handleFieldChange}
              highlighted={props.highlightedErrorFields.has(field.key)}
              disabled={!props.isFieldEditable(field)}
              customerPicker={props.customerPicker}
              reasonControls={props.reasonControls}
            />
          ))}
        </FieldGroup>
      ) : null}
    </div>
  );
}

function FieldGroup(props: {
  label?: string | undefined;
  className: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-2">
      {props.label ? (
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {props.label}
        </div>
      ) : null}
      <div className={props.className}>{props.children}</div>
    </section>
  );
}

function FieldTile(props: {
  field: ExceptionField;
  fieldValues: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
  highlighted: boolean;
  disabled: boolean;
  customerPicker: CustomerPickerState;
  reasonControls: FieldReasonControls;
}): JSX.Element {
  const { field, reasonControls } = props;
  const isKeyField = KEY_HEADER_FIELD_KEYS.includes(field.key);
  const reasonKey = isKeyField ? fieldReasonKey({ fieldName: field.key, lineNumber: null }) : null;
  const isMissingReason = reasonKey ? reasonControls.missingReasonKeys.has(reasonKey) : false;
  const isPopoverOpen = reasonKey ? reasonControls.openReasonKey === reasonKey : false;
  return (
    <div
      data-field-key={field.key}
      className={cn(
        'flex h-full min-w-0 flex-col gap-1.5 rounded-md border bg-muted/20 p-3',
        field.needsReview && 'border-warning/60 bg-warning/5',
        props.highlighted && 'border-destructive bg-destructive/5',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Label htmlFor={field.key}>{field.label}</Label>
          {field.reviewMessage ? <InfoHoverCard message={field.reviewMessage} /> : null}
          {reasonKey && isMissingReason ? (
            <FieldReasonPopover
              reasonKey={reasonKey}
              label={field.label}
              open={isPopoverOpen}
              draft={reasonControls.fieldReasonDrafts[reasonKey] ?? ''}
              onDraftChange={(draft) => reasonControls.setReasonDraft(reasonKey, draft)}
              onCommit={(text) => reasonControls.commitReason(reasonKey, text)}
              onDismiss={() => reasonControls.dismissReasonPopover()}
              anchor={
                <span>
                  <MissingFeedbackBadge
                    onClick={() => reasonControls.requestReasonImmediate(reasonKey, field.label)}
                  />
                </span>
              }
            />
          ) : null}
        </div>
        <ConfidenceBadge value={field.confidence} lowVariant={fieldLowVariant(field)} />
      </div>
      <FieldValueEditor
        field={field}
        fields={props.fieldValues}
        onFieldChange={props.onFieldChange}
        disabled={props.disabled}
        customerPicker={props.customerPicker}
      />
    </div>
  );
}

function previousHeaderValue(field: ExceptionField): string {
  if (field.key === 'requested_delivery_date') return field.value ?? '';
  return field.matchedValue ?? field.value ?? '';
}

function fieldLowVariant(field: ExceptionField): 'warning' | 'destructive' {
  if (
    field.key === 'payment_terms' ||
    field.key === 'delivery_terms' ||
    field.key === 'delivery_mode'
  ) {
    return 'warning';
  }
  return 'destructive';
}
